import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { GeckoWeld } from './geckoWelds'

export interface GeckoPipeCircumferentialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.PipeCircumferentialWeld
  feature_geometry: {
    y: number
  }
}
export const NewGeckoPipeCircumferentialWeldJSON: GeckoPipeCircumferentialWeldJSON =
  {
    name: 'New Pipe Circumferential Weld',
    type: GeckoFeatureModelType.PipeCircumferentialWeld,
    feature_geometry: {
      y: 0,
    },
  }

export class GeckoPipeCircumferentialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.PipeCircumferentialWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoPipeCircumferentialWeldJSON,
    componentGeometry?: GeckoComponentRect
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    if (componentGeometry) {
      feature_geometry.x_start = componentGeometry.start.x.num
      feature_geometry.x_end = componentGeometry.end.x.num
    }
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x_start as number,
      feature_geometry.y as number,
      feature_geometry.x_end as number,
      feature_geometry.y as number
    )
  }

  public static CreateMapFromJSON(
    component_id: string,
    recordsJson: Record<string, GeckoPipeCircumferentialWeldJSON>,
    componentGeometry: GeckoComponentRect
  ): Map<string, GeckoPipeCircumferentialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoPipeCircumferentialWeld(
          component_id,
          weldID,
          recordsJson[weldID],
          componentGeometry
        )
      )
      return resultObj
    }, new Map<string, GeckoPipeCircumferentialWeld>())
  }
  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoPipeCircumferentialWeldJSON
  ): GeckoPipeCircumferentialWeld {
    return new GeckoPipeCircumferentialWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generatePipeCircumferentialWeld(
      this.name,
      this.feature_geometry.y.min
    )
  }
}

export function generatePipeCircumferentialWeld(
  name: string | undefined,
  y: GeckoMeasurement
): GeckoPipeCircumferentialWeldJSON {
  const PipeCircumferentialWeldJSON: GeckoPipeCircumferentialWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.PipeCircumferentialWeld,
    feature_geometry: {
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return PipeCircumferentialWeldJSON
}

export const PipeCircumferentialWeldFormFields = (
  weldJSON: GeckoPipeCircumferentialWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: weldJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PipeCircumferentialWeldJSONFromFormFields = (
  formFields: FormField[]
) => {
  return generatePipeCircumferentialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const PipeCircumferentialWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PipeCircumferentialWeld,
  title: 'Pipe Circumferential Welds',
  singularName: 'Pipe Circumferential Weld',
  fields: ['y'],
}
